import { ReactNode, useEffect, useRef, useState, useCallback } from "react";
import cn from "classnames";
import { Field, useFormState, useForm } from "react-final-form";
import { InputRef, message, Modal } from "antd";
import {
  CustomButton,
  DescriptionCollapseField,
} from "~/components";
import { NamesOfFieldEnum, PayformLinkType, WebsiteType } from "~/typings/types";
import styles from "./SocialLinkWithModalField.module.scss";
import { CustomTooltip } from "~/components/title-field/CustomTooltip";
import { SocialFieldInput } from "./components";
import { composeValidators, disabledValidate, validateDependentFields } from "~/utils";
import { CopyOutlined } from "@ant-design/icons";
import { configPlatform } from "./components/social-field-input";
import { postValidateWebsites } from "../../v2/customer/api/apiMethods";
import { ErrorField, RenderError } from "~/components/error-field";
import { ERROR_FIELD_ } from "~/configs/consts";

const tooltipMsg =
  "Интернет-ресурс обязательно должен быть открытым и доступным. Также он должен содержать описание услуг или продуктов, за которые вы планируете принимать оплату через Prodamus.";

export const WebsiteMapping = {
  toWebsiteType: {
    [NamesOfFieldEnum.PayformLinkVk]: "vk",
    [NamesOfFieldEnum.PayformLinkTg]: "telegram",
    [NamesOfFieldEnum.PayformLinkInstagram]: "instagram",
    [NamesOfFieldEnum.PayformLinkSite]: "site",
    [NamesOfFieldEnum.PayformLinkOther]: "other",
  } as const,
  toPayformLink: {
    vk: NamesOfFieldEnum.PayformLinkVk,
    telegram: NamesOfFieldEnum.PayformLinkTg,
    instagram: NamesOfFieldEnum.PayformLinkInstagram,
    site: NamesOfFieldEnum.PayformLinkSite,
    other: NamesOfFieldEnum.PayformLinkOther,
  } as const,
};

export const mapPayformLinkTypeToWebsiteType = (
  payformType: PayformLinkType
): WebsiteType => WebsiteMapping.toWebsiteType[payformType];

export const mapWebsiteTypeToPayformLink = (
  websiteType: WebsiteType
): PayformLinkType => WebsiteMapping.toPayformLink[websiteType];

const payformLinks: PayformLinkType[] = [
  NamesOfFieldEnum.PayformLinkSite,
  NamesOfFieldEnum.PayformLinkTg,
  NamesOfFieldEnum.PayformLinkVk,
  NamesOfFieldEnum.PayformLinkInstagram,
  NamesOfFieldEnum.PayformLinkOther,
];

interface SocialLinkWithModalFieldProps {
  name: string;
  titleField?: string;
  descriptionContent?: ReactNode;
  required?: boolean;
}

export const SocialLinkWithModalField = ({
  name,
  titleField,
  descriptionContent,
  required,
}: SocialLinkWithModalFieldProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [mapErrors, setMapErrors] = useState<Partial<Record<PayformLinkType, RenderError>>>({});
  const initialValuesRef = useRef<Record<PayformLinkType, string>>();
  const isSavingRef = useRef(false);

  const form = useForm();
  const { errors, submitErrors, submitFailed, values } = useFormState({
    subscription: { errors: true, submitErrors: true, submitFailed: true, values: true },
  });

  /** Очистка ошибок при удалении значения */
  useEffect(() => {
    setMapErrors((prev) => {
      const next = { ...prev };
      for (const platform of payformLinks) {
        const value = String(values?.[platform] || "").trim();
        if (!value && next[platform]) {
          delete next[platform];
        }
      }
      return next;
    });
  }, [values]);

  const validateWebsites = useCallback(async () => {
    if (isValidating) return;

    const currentValues = form.getState().values;
    const websites = payformLinks
      .map((platform) => ({
        type: mapPayformLinkTypeToWebsiteType(platform),
        url: String(currentValues?.[platform] || "").trim(),
      }))
      .filter((w) => w.url);

    if (websites.length === 0) {
      message.warning("Необходимо указать хотя бы одну ссылку");
      return;
    }

    setIsValidating(true);
    try {
      const res = await postValidateWebsites({ body: { websites } });

      if (res.data?.success && Array.isArray(res.data.results)) {
        res.data.results.forEach((item: any) => {
          const payformKey = mapWebsiteTypeToPayformLink(item.type);
          if (item.valid) {
            form.change(payformKey, item.normalized_url);
          }
        });

        isSavingRef.current = true;
        handleCloseModal();
      } else {
        message.error("Ошибка при проверке ссылок");
      }
    } catch (error: any) {
      message.error("Не удалось проверить ссылки");

      const apiResults = error?.response?.data?.results;
      if (Array.isArray(apiResults)) {
        const nextErrors: Partial<Record<PayformLinkType, RenderError>> = {};
        apiResults.forEach((item) => {
          const payformKey = mapWebsiteTypeToPayformLink(item.type);
          if (item.valid) {
            form.change(payformKey, item.normalized_url);
          } else {
            nextErrors[payformKey] = {
              has: 1,
              message: item.errors || "Некорректная ссылка",
            };
          }
        });
        setMapErrors(nextErrors);
      }
    } finally {
      setIsValidating(false);
    }
  }, [form]);

  const handleClearField = useCallback(
    (platform: PayformLinkType): void => {
      form.change(platform, undefined);
      setMapErrors((prev) => {
        const next = { ...prev };
        delete next[platform];
        return next;
      });
    },
    [form]
  );

  const handleCopyToClipboard = useCallback(async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      message.success("Скопировано в буфер обмена");
    } catch {
      message.error("Не удалось скопировать");
    }
  }, []);

  const handleOpenModal = useCallback((): void => {
    const currentValues = form.getState().values;

    const snapshot: Record<PayformLinkType, string> = {} as any;

    payformLinks.forEach((platform) => {
      snapshot[platform] = String(currentValues?.[platform] || "");
    });

    initialValuesRef.current = snapshot;

    setIsModalOpen(true);
  }, [form]);

  const handleCloseModal = useCallback((): void => {
    if (!isSavingRef.current) {
      const snapshot = initialValuesRef.current;

      payformLinks.forEach((platform) => {
        snapshot && form.change(platform, snapshot?.[platform] || undefined);
      });

      setMapErrors({});
    }

    isSavingRef.current = false;
    setIsModalOpen(false);
  }, [form]);

  const handleSave = useCallback((): void => {
    const hasAnyValue = payformLinks.some((p) => !!String(values?.[p] || "").trim());
    if (!hasAnyValue) {
      message.warning("Укажите хотя бы одну ссылку перед сохранением");
      return;
    }
    validateWebsites();
  }, [values, validateWebsites]);

  return (
    <>
      <Field
        name={name}
        validate={composeValidators(
          validateDependentFields(
            payformLinks,
            "Один из источников должен быть обязательно заполнен"
          )
        )}
      >
        {({ meta }) => {
          const hasError =
            ((meta?.error || meta?.submitError) && meta?.submitFailed);

          const inputId = hasError ? `${ERROR_FIELD_}${name}` : undefined;

          return (
            <button
              id={inputId}
              className={cn(styles.buttonField, { [styles.buttonFieldError]: hasError })}
              type="button"
              onClick={handleOpenModal}
            >
              <div className={styles.fieldHeader}>
                <span className={cn({ [styles.text]: required })}>
                  {titleField}
                </span>
                <CustomTooltip tooltip={tooltipMsg} />
              </div>
            </button>
          );
        }}
      </Field>

      {payformLinks.some((platform) => values?.[platform]) && (
        <div className={styles.addedSocialsBox}>
          <p className={styles.addedSocialsTitle}>Добавленные ссылки:</p>
          <div className={styles.addedSocialsIcons}>
            {payformLinks.map((platform) => {
              const value = values?.[platform];
              if (!value) return null;

              const config = configPlatform[platform];
              const hasError = errors?.[platform] || submitErrors?.[platform];

              return (
                <div
                  key={platform}
                  className={cn(styles.addedSocialItem, {
                    [styles.error]: hasError,
                    [styles.iconOther]:
                      platform === NamesOfFieldEnum.PayformLinkOther,
                  })}
                  title={config?.title}
                >
                  {config?.icon}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className={styles.requirementsBox}>
        <h4 className={styles.requirementsTitle}>Важно! Требования к ресурсам:</h4>
        <div className={styles.requirementsList}>
          <p><strong>1. Telegram-канал или бот:</strong></p>
          <ul>
            <li>Канал должен быть открытым и доступным.</li>
            <li>
              К закрытому каналу должен быть предоставлен доступ. Добавьте наш аккаунт{" "}
              <>
                <span
                  className={styles.copyLink}
                  onClick={() => handleCopyToClipboard("@prodamus_checker")}
                >
                  @prodamus_checker
                </span>
                <CopyOutlined
                  className={styles.copyIcon}
                  onClick={() => handleCopyToClipboard("@prodamus_checker")}
                />
                {" "}в участники канала, чтобы мы проверили ваш ресурс.
              </>
            </li>
            <li>
              В Telegram-канале и боте обязательно должны быть указаны данные поставщика услуг (ФИО, ИНН, контактные данные- в шапке профиля или в закрепленном посте) и прикреплена оферта.
            </li>
          </ul>

          <p><strong>2. Сайт и иные соцсети:</strong></p>
          <ul>
            <li>Для сайта обязательно наличие ФИО, ИНН, контактных данных.</li>
            <li>В соцсетях должны быть указаны ваши ФИО (по паспорту) и номер ИНН.</li>
          </ul>
        </div>
      </div>

      <ErrorField name={name} />

      <Modal
        title={titleField}
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={630}
        afterClose={() => window.scrollTo(0, 0)}
      >
        <p className={styles.titleDescription}>{tooltipMsg}</p>

        <div className={styles.socialFieldsList}>
          {payformLinks.map((platform) => (
            <SocialFieldInput
              key={platform}
              platform={platform}
              renderError={mapErrors?.[platform]}
              onClear={() => handleClearField(platform)}
            />
          ))}
        </div>

        <DescriptionCollapseField
          content={descriptionContent}
          trigger={payformLinks.length > 0}
          tooltipMsg={tooltipMsg}
        />

        <p className={styles.titleDescription} style={{ fontStyle: "italic" }}>
          *принадлежит Meta, признанной экстремистской организацией и запрещённой на территории РФ
        </p>

        <div className={styles.modalFooter}>
          <CustomButton
            name="Сохранить"
            type="button"
            onClick={handleSave}
            isLoading={isValidating}
            disabled={isValidating}
          />
        </div>
      </Modal>
    </>
  );
};
